import { WidgetType } from 'types/widget.types';
import { isFiniteNumber } from 'utils/number';
import { VERSION } from './WordCloudWidget.upgrade';
import { WordCloudWidgetData } from './WordCloudWidget.types';
import {
  DEFAULT_HEIGHT,
  DEFAULT_WORD_CLOUD_CHART_DATA,
  DEFAULT_WIDTH,
  PLACEMENT_DIVIDER,
  PLACEMENT_LEFT_INDEX,
  PLACEMENT_TOP_INDEX,
} from './WordCloudWidget.config';

/**
 * Generate default PieChartWidget data
 */
export const generateDefaultData = () => {
  return {
    widgetType: WidgetType.WordCloudChart,
    widgetData: {
      version: VERSION,
      widthPx: DEFAULT_WIDTH,
      heightPx: DEFAULT_HEIGHT,
      topPx: 0,
      leftPx: 0,
      rotateDeg: 0,
      altText: '',
      isDecorative: false,
      patterns: {
        enabled: false,
        list: [],
      },

      ...DEFAULT_WORD_CLOUD_CHART_DATA,
    } as WordCloudWidgetData,
  };
};

/**
 * Provide a position based on the index of the data. No specific algorithm is used, so feel free to change it.
 * Cuurently it is used to add abilities to change the position if the user change the word order on the table.
 *
 * @function getRandomPosition
 * @see https://github.com/highcharts/highcharts/blob/master/ts/Series/Wordcloud/WordcloudUtils.ts#L428
 *
 * @param {number} size Random factor.
 * @param {number} index index of the data.
 * @return {number} Random position.
 */
export const getRandomPosition = (index: number, direction: 'x' | 'y'): number => {
  const positionOffdet = index / PLACEMENT_DIVIDER;
  const placementIndex = index % PLACEMENT_DIVIDER;

  const indexDictionary = direction === 'x' ? PLACEMENT_LEFT_INDEX : PLACEMENT_TOP_INDEX;
  const numberSign = indexDictionary.has(placementIndex) ? -1 : 1;
  return numberSign * positionOffdet;
};

/**
 * Calculates a number of degrees to rotate, based upon a number of
 * orientations within a range from-to.
 *
 * @function getRotation
 * @see https://github.com/highcharts/highcharts/blob/master/ts/Series/Wordcloud/WordcloudUtils.ts#L559
 *
 * @param {number} [orientations] Number of orientations.
 * @param {number} [index] Index of point, used to decide orientation.
 * @param {number} [from] The smallest degree of rotation.
 * @param {number} [to] The largest degree of rotation.
 * @return {boolean | number} Returns the resulting rotation for the word. Returns false if invalid input parameters.
 */
export const getRotation = (orientations?: number, index?: number, from?: number, to?: number): boolean | number => {
  let result: boolean | number = false, // Default to false
    range: number,
    intervals: number,
    orientation: number;

  // Check if we have valid input parameters.
  if (
    isFiniteNumber(orientations) &&
    isFiniteNumber(index) &&
    isFiniteNumber(from) &&
    isFiniteNumber(to) &&
    orientations > 0 &&
    index > -1 &&
    to > from
  ) {
    range = to - from;
    intervals = range / (orientations - 1 || 1);
    orientation = index % orientations;
    result = from + orientation * intervals;
  }
  return result;
};
